/**
 * Module-level (in-memory, never persisted) state for a Rocket.Chat outage.
 *
 * initRocketChat() (AuthContext.tsx) sets this when POST /api/chat/token
 * answers 503 CHAT_UNAVAILABLE (backend/src/routes/rocketchat.ts). ChatWindow
 * reads it to show a friendly "Chat is temporarily unavailable" message
 * instead of the generic "Team Chat not connected" placeholder.
 *
 * Owner ruling (Tony, 2026-10-05): the friendly message shows only ONCE per
 * page load — after that, chat UI stays in its quiet disabled state without
 * repeating the notice. This is intentionally a plain module-level variable,
 * not localStorage: it must reset naturally on the next page load, never
 * persist across reloads/sessions.
 */

export type ChatUnavailableReason = 'user_limit' | 'unreachable' | 'not_configured'

let reason: ChatUnavailableReason | null = null
let noticeShown = false
let listeners: Array<() => void> = []

export function setChatUnavailable(r: ChatUnavailableReason | null) {
  reason = r
  listeners.forEach((l) => l())
}

export function getChatUnavailableReason(): ChatUnavailableReason | null {
  return reason
}

export function onChatUnavailableChange(cb: () => void): () => void {
  listeners.push(cb)
  return () => {
    listeners = listeners.filter((l) => l !== cb)
  }
}

/**
 * Returns true the first time it's called after chat became unavailable this
 * page load; false every time after. Callers use this to gate showing the
 * one-time friendly notice.
 */
export function consumeChatUnavailableNotice(): boolean {
  if (noticeShown) return false
  noticeShown = true
  return true
}

/** Test-only reset — never call from app code. */
export function __resetChatUnavailableStoreForTests() {
  reason = null
  noticeShown = false
  listeners = []
}
